/**
 * @author <ziad.khoury.hanna@oceandatalab.com>
 *
 * Based on code that is Copyright (c) 2015 Dan Abramov
 * https://github.com/gaearon/react-side-effect
 *
 * @flow
 */
export function withSideEffect/*:: <Props, State> */(
  h /*: React$CreateElement */,
  Component /*: typeof React$Component */,
  reducePropsToState /*: (props: Props[]) => State */,
  onStateChange /*: (state: State) => void */
) /*: (
  WrappedComponent: React$ComponentType<Props>
) => React$Component<Props, State> */ {
  if (typeof reducePropsToState !== 'function') {
    throw new Error('Expected reducePropsToState to be a function.');
  }
  if (typeof onStateChange !== 'function') {
    throw new Error('Expected onStateChange to be a function.');
  }

  function getDisplayName(WrappedComponent /*: {
    +displayName?: string,
    +name?: string,
  } */) {
    return WrappedComponent.displayName || WrappedComponent.name || 'Component';
  }

  // $FlowIgnore
  return function wrap(WrappedComponent /*: React$ComponentType<Props> */) {
    if (typeof WrappedComponent !== 'function') {
      throw new Error('Expected WrappedComponent to be a React component.');
    }

    const mountedInstances /*: SideEffect[] */ = [];
    let state;

    function emitChange() {
      state = reducePropsToState(
        mountedInstances.map(function(instance /*: SideEffect */) {
          return instance.props;
        })
      );

      onStateChange(state);
    }

    class SideEffect extends Component /*:: <Props, State> */ {
      // Try to use displayName of wrapped component
      static displayName = `SideEffect(${getDisplayName(WrappedComponent)})`;

      static peek() /*: State */ {
        return state;
      }

      componentWillMount() {
        mountedInstances.push(this);
        emitChange();
      }

      componentDidUpdate() {
        emitChange();
      }

      componentWillUnmount() {
        const index = mountedInstances.indexOf(this);

        mountedInstances.splice(index, 1);
        emitChange();
      }

      render() {
        return h(WrappedComponent, this.props);
      }
    }

    return SideEffect;
  };
}
